/**
 * Mithai box labels — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_diwali`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const mithaiBoxLabelsPack: ToilePack = {
    slug: 'mithai-box-labels',
    category: 'faith',
    name: 'Mithai box labels',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'kaju', label: 'Kaju katli', subject: 'a sheet of six round labels reading "Kaju Katli" with a diamond of silver-leafed sweet drawn on each and a blank "From" line'},
        {key: 'ladoo', label: 'Motichoor ladoo', subject: 'a sheet of six rectangular labels reading "Motichoor Ladoo" with a small pile of orange ladoos and a marigold border'},
        {key: 'assorted', label: 'Assorted sweets', subject: 'a sheet of four wide labels reading "Assorted Mithai" with a row of barfi, jalebi and gulab jamun and a blank line for the date'},
    ],
    styles: [
        {key: 'kraft', label: 'Kraft', style: 'labels in dark ink and cream as if printed on kraft stock, hand-drawn sweet sketches', palette: 'kraft brown with cream and dark brown ink', framing: 'Flat on the sheet, labels evenly spaced'},
        {key: 'vintage', label: 'Vintage tin', style: 'vintage lithograph tin label, ornamental border, decorative serif lettering', palette: 'maroon, gold and cream', framing: 'Labels on a plain cream ground, evenly spaced'},
        {key: 'flatcolor', label: 'Flat color', style: 'flat vector labels, bold clean shapes, hard edges, no outlines', palette: 'saffron, pink, green and white, four colors', framing: 'Labels on a plain white ground, evenly spaced'},
    ],
    directions: ['Barfi', 'Wedding', 'Gold foil'],
    directionHint: 'try: Barfi',
    seo: {
        title: 'Mithai box labels: kaju katli, ladoo, assorted sweets',
        metaDescription: "Printable mithai box labels with the sweet's name lettered exactly and a From line, for kaju katli, ladoo or assorted sweets, in three styles.",
        h1: 'Mithai box labels: kaju katli, motichoor ladoo, assorted sweets',
        tagline: "Print-ready label sheets, each sticker with its sweet's name lettered exactly and a line for who it is from.",
        intro: "Labels to stick on boxes of homemade or bought mithai given at Diwali, Raksha Bandhan, a wedding or Eid. Each sheet is an even grid of labels with the sweet's name lettered exactly: Kaju Katli on round labels, Motichoor Ladoo on rectangular labels with a marigold border, and Assorted Mithai on wide labels with barfi, jalebi and gulab jamun. Each has a line for who the box is from. The styles are kraft, vintage tin and flat colour.",
        useCases: [
            'a box of sweets given at Diwali',
            'a gift box for Raksha Bandhan or Eid',
            'a box for a wedding',
            'a label for homemade sweets',
        ],
        faq: [
            {q: 'What is written on each label?', a: "The sweet's name, lettered as written, and a line for who the box is from. The assorted labels also have a line for the date."},
            {q: 'Which occasions do the labels suit?', a: 'Diwali, Raksha Bandhan, a wedding or Eid, since the name and the From line do not refer to one festival.'},
            {q: 'How do I print and cut them?', a: 'Print the sheet and cut the labels out along the spaces between them. The labels are evenly spaced on the page.'},
            {q: 'Can I ask for a different sweet?', a: 'Yes. The direction field takes a change such as Barfi, Wedding or Gold foil. A direction you type is a request, not a promised result.'},
        ],
    },
}
